/*!
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */

import classNames from 'classnames';

import { type PolymorphicCommonProps } from '../_common/types';
import { ConditionalWrap } from '../conditional-wrap';
import { Tooltip } from '../tooltip';

type CodeProps = {
  /** The content of the code */
  children?: React.ReactNode;
  /** Whether the code is disabled */
  isDisabled?: boolean;
  /** Whether the code is runnable */
  isRunnable?: boolean;
  /** The description of the code, will be displayed in a tooltip */
  description?: string;
  /** The callback function triggered when the inline code is clicked */
  onClick?: (
    event:
      | React.MouseEvent<HTMLElement, MouseEvent>
      | React.KeyboardEvent<HTMLElement>,
  ) => void;
};

export const Code = <T extends React.ElementType = 'button'>({
  children,
  className,
  isDisabled = false,
  style,
  isRunnable = false,
  description,
  onClick,
  as,
  ref,
  htmlAttributes,
  ...restProps
}: PolymorphicCommonProps<T, CodeProps>) => {
  const Component: React.ElementType = as ?? (onClick ? 'button' : 'span');

  const classes = classNames(
    'ndl-code',
    {
      'ndl-disabled': isDisabled,
      'ndl-runnable': isRunnable,
    },
    className,
  );

  const handleClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (isDisabled) {
      event.preventDefault();
      return;
    }

    onClick?.(event);
  };

  return (
    <ConditionalWrap
      shouldWrap={Boolean(description) === true}
      wrap={(children) => (
        <Tooltip type="simple">
          <Tooltip.Trigger hasButtonWrapper={Component === 'button'}>
            {children}
          </Tooltip.Trigger>
          <Tooltip.Content>{description}</Tooltip.Content>
        </Tooltip>
      )}
    >
      <Component
        className={classes}
        style={style}
        onClick={handleClick}
        disabled={isDisabled}
        ref={ref}
        {...restProps}
        {...htmlAttributes}
      >
        <code>{children}</code>
      </Component>
    </ConditionalWrap>
  );
};
